import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CloseIcon } from '../Icons';
import ticketService from '../../services/ticketService';

const MAX_TICKET_IMAGE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TICKET_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif'
]);
const ALLOWED_TICKET_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];
const formatFileSize = (size = 0) => {
  if (!size) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let index = 0;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
};

const isAllowedTicketImage = (file) => {
  const name = String(file?.name || '').toLowerCase();
  const hasAllowedExtension = ALLOWED_TICKET_IMAGE_EXTENSIONS.some(ext => name.endsWith(ext));
  return ALLOWED_TICKET_IMAGE_TYPES.has(file?.type) && hasAllowedExtension;
};

const createPreviewUrl = (file) => (
  typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(file)
    : ''
);

const revokePreviewUrl = (image) => {
  if (image?.preview_url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(image.preview_url);
  }
};

const TicketCommentComposer = ({ ticketId, onUpdate, disabled, saving, onBusyChange }) => {
  const [message, setMessage] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(-1);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const imagesRef = useRef([]);
  const savingRef = useRef(false);
  const selectedPreview = previewIndex >= 0 ? selectedImages[previewIndex] : null;
  const submitDisabled = disabled || !message.trim();
  const movePreview = useCallback(direction => {
    if (selectedImages.length > 1) setPreviewIndex(index => (index + direction + selectedImages.length) % selectedImages.length);
  }, [selectedImages.length]);

  useEffect(() => { imagesRef.current = selectedImages; }, [selectedImages]);
  useEffect(() => () => imagesRef.current.forEach(revokePreviewUrl), []);
  useEffect(() => {
    if (previewIndex >= selectedImages.length) setPreviewIndex(-1);
  }, [previewIndex, selectedImages.length]);
  useEffect(() => {
    if (!selectedPreview) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') setPreviewIndex(-1);
      if (event.key === 'ArrowLeft') movePreview(-1);
      if (event.key === 'ArrowRight') movePreview(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPreview, movePreview]);
  const addImages = fileList => {
    if (disabled) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (selectedImages.length + files.length > 20) { setError('Attach up to 20 images per comment.'); return; }
    const invalid = files.find(file => !isAllowedTicketImage(file) || file.size > MAX_TICKET_IMAGE_SIZE);
    if (invalid) { setError(`${invalid.name} must be a supported image file and 10MB or smaller.`); return; }
    setError('');
    setNotice('');
    setSelectedImages(images => [...images, ...files.map((file, index) => ({
      temp_key: `${Date.now()}-${index}-${file.name}`, name: file.name, size: file.size,
      preview_url: createPreviewUrl(file), file
    }))]);
  };
  const removeImage = key => setSelectedImages(images => {
    images.filter(image => image.temp_key === key).forEach(revokePreviewUrl);
    return images.filter(image => image.temp_key !== key);
  });
  const send = async () => {
    if (submitDisabled || savingRef.current) return;
    savingRef.current = true;
    onBusyChange(true);
    setError('');
    setNotice('');
    try {
      const result = await ticketService.addComment(ticketId, message.trim(), selectedImages);
      if (!result.success) { setError(result.message || 'Unable to send comment.'); return; }
      onUpdate(result.data);
      setMessage('');
      setSelectedImages(images => { images.forEach(revokePreviewUrl); return []; });
      setNotice('Comment sent.');
    } catch (error) {
      setError(error.message || 'Unable to send comment.');
    } finally {
      savingRef.current = false;
      onBusyChange(false);
    }
  };

  return <>
    <form className={`ticket-chat-composer${isDraggingImages ? ' is-dragging' : ''}`} aria-label="Add comment"
      onSubmit={event => { event.preventDefault(); send(); }}
      onDragOver={event => { event.preventDefault(); if (!disabled) setIsDraggingImages(true); }}
      onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setIsDraggingImages(false); }}
      onDrop={event => { event.preventDefault(); setIsDraggingImages(false); addImages(event.dataTransfer.files); }}>
      {error && <p role="alert" className="ticket-inline-notice is-error">{error}</p>}
      {notice && <span role="status" className="ticket-visually-hidden">{notice}</span>}
      <fieldset className="ticket-composer-fields" disabled={disabled}>
        <label htmlFor="ticket-comment" className="ticket-visually-hidden">Message</label>
        <textarea id="ticket-comment" className="input ticket-chat-input" rows="2" required value={message}
          placeholder="Write a message or paste a screenshot..."
          onChange={event => { setMessage(event.target.value); setError(''); setNotice(''); }}
          onKeyDown={event => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); send(); }
          }}
          onPaste={event => {
            const images = Array.from(event.clipboardData.files || []).filter(file => file.type.startsWith('image/'));
            if (images.length && !disabled) { event.preventDefault(); addImages(images); }
          }} />
        {selectedImages.length > 0 && <div className="ticket-composer-images">
          {selectedImages.map((image, index) => <div className="ticket-composer-image" key={image.temp_key}>
            <button type="button" onClick={() => setPreviewIndex(index)} aria-label={`Preview ${image.name}`}><img src={image.preview_url} alt={image.name} /></button>
            <button type="button" className="ticket-composer-image-remove" onClick={() => removeImage(image.temp_key)} aria-label={`Remove ${image.name}`}><CloseIcon /></button>
          </div>)}
        </div>}
        <div className="ticket-chat-toolbar">
          <div className="ticket-chat-tools">
            <label className={`ticket-chat-attach${disabled ? ' is-disabled' : ''}`} title="Up to 20 images, 10MB each. JPG, PNG, GIF, WebP, HEIC.">
              <input type="file" className="ticket-visually-hidden" aria-label="Attach images"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif" multiple
                onChange={event => { addImages(event.target.files); event.target.value = ''; }} />
              Attach images{selectedImages.length ? ` (${selectedImages.length})` : ''}
            </label>
            <span className="ticket-chat-shortcut">Ctrl/⌘ + Enter to send</span>
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitDisabled}>{saving ? 'Sending...' : 'Send'}</button>
        </div>
      </fieldset>
    </form>
  {selectedPreview && (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 1200, backgroundColor: 'rgba(var(--neutral-rgb), 0.82)', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) auto', padding: '1rem' }}
      onClick={() => setPreviewIndex(-1)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', color: '#fff' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.92rem', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedPreview.name}</div>
          <div style={{ marginTop: '0.2rem', fontSize: '0.75rem', opacity: 0.78 }}>{previewIndex + 1} / {selectedImages.length} | {formatFileSize(selectedPreview.size)}</div>
        </div>
        <button type="button" className="btn" style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)' }} onClick={(event) => { event.stopPropagation(); setPreviewIndex(-1); }}>
          Close
        </button>
      </div>

      <div style={{ position: 'relative', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem 3.25rem' }} onClick={(event) => event.stopPropagation()}>
        {selectedImages.length > 1 && (
          <button type="button" aria-label="Previous image" className="btn" style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '42px', height: '42px', padding: 0, color: '#fff', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)' }} onClick={() => movePreview(-1)}>
            {'<'}
          </button>
        )}
        <img src={selectedPreview.preview_url} alt={selectedPreview.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }} />
        {selectedImages.length > 1 && (
          <button type="button" aria-label="Next image" className="btn" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: '42px', height: '42px', padding: 0, color: '#fff', borderColor: 'rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)' }} onClick={() => movePreview(1)}>
            {'>'}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', flexWrap: 'wrap' }} onClick={(event) => event.stopPropagation()}>
        {selectedImages.map((image, imageIndex) => (
          <button
            key={`preview-thumb-${image.temp_key || image.name}`}
            type="button"
            aria-label={`Open image ${imageIndex + 1}`}
            onClick={() => setPreviewIndex(imageIndex)}
            style={{ width: '54px', height: '40px', padding: 0, borderRadius: '6px', overflow: 'hidden', border: imageIndex === previewIndex ? '2px solid #fff' : '1px solid rgba(255,255,255,0.35)', backgroundColor: 'rgba(255,255,255,0.12)', cursor: 'pointer' }}
          >
            <img src={image.preview_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </button>
        ))}
      </div>
    </div>
  )}
  </>;
};

export default TicketCommentComposer;
